import { getCSRFToken } from '@mortgage-pos/utils/tokens'
import { gql } from '@apollo/client'
import groupBy from 'lodash/groupBy'
import { history } from '@mortgage-pos/ui/history'
import { StoreModel } from './index'
import { AxiosResponse } from 'axios'
import rollbar from '@mortgage-pos/ui/services/rollbar'
import http, { graphQL } from '@mortgage-pos/ui/services/http'
import { environment } from '@mortgage-pos/ui/env'
import { getLandingUrl } from '@mortgage-pos/ui/utils/landingUrl'
import { getReferrerUrl } from '@mortgage-pos/ui/utils/referrerUrl'
import { variation } from '@mortgage-pos/ui/services/featureFlags'
import { getGraphQLString, redirectTo } from '@mortgage-pos/utils'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { CustomError } from '@mortgage-pos/types'
import ApplicationClient from '@mortgage-pos/ui/services/ApplicationClient'
import {
  isQuestionVisible,
  isVisibleIfConstructor,
} from '@mortgage-pos/ui/utils/visibleIf'
import { removeUnwantedAnswers } from '@mortgage-pos/ui/utils/applicationHelpers'
import { isMiniFlowActive } from '@mortgage-pos/ui/utils/exitToBorrowerPortal'
import incense from '@mortgage-pos/ui/incense'

import {
  action,
  Action,
  actionOn,
  ActionOn,
  Computed,
  computed,
  Thunk,
  thunk,
  thunkOn,
  ThunkOn,
} from 'easy-peasy'

import {
  Page,
  Status,
  ErrorPage,
  OtherPage,
  LoanPurpose,
  PurchaseStage,
  QuestionSet,
  SuccessPage,
  RateSelectPage,
  QuestionSetSlug,
  QuestionnaireType,
  SectionStatusCode,
  RedirectInterstitial,
  Routes,
  MessageTypes,
  RhDeeplinkFlow,
} from '@mortgage-pos/types'
import { redirectToOtherQuestionSet } from '@mortgage-pos/ui/utils/navigation'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import { enableMapSet } from 'immer'
import { getRedirectToken } from '@mortgage-pos/ui/services/robinhood'
import {
  getMonarchEvaluationResult,
  evaluateConsentExperiment,
} from '../utils/monarchHelpers'
import { simpleV2TrackingService } from '@mortgage-pos/shared-frontend'

// This helps [Unit Tests] to add data to the redux store
enableMapSet()

export interface InitializeQuestionnairePayload {
  questionSet: QuestionSet
}

export interface StatusesMap {
  lock?: Status
  rates?: Status
  noRates?: Status
  completed?: Status
  touched?: Status
  leadSubmitted?: Status
  manualReview?: Status
}

interface CloseMiniFlowPayload {
  parentMessage?: {
    name: string
    payload?: Record<string, any>
  }
}

interface WindowWithRobinhood extends Window {
  GoldSageJavaScriptInterface?: {
    onSageExit: (payload: string) => void
  }
  webkit?: {
    messageHandlers?: {
      onSageExit?: {
        postMessage: (payload: any) => void
      }
    }
  }
  android?: {
    onSageExit: (payload: string) => void
  }
}

type RobinhoodBridge =
  | { platform: 'ios'; iosHandler: { postMessage: (payload: any) => void } }
  | {
      platform: 'android'
      androidHandler: { onSageExit: (payload: string) => void }
    }

/**
 * Reads the Robinhood native bridge from `window` at call time.
 *
 * Detection MUST be live (not a memoized `computed`): Robinhood injects the
 * bridge asynchronously, after render, so a slug-only computed can cache a stale
 * `false`. Correctness-critical handoffs call this at click time instead.
 *
 * The Android bridge is either `window.android` or the Gold shell's
 * `GoldSageJavaScriptInterface`. We select whichever actually exposes an
 * `onSageExit` function — one object can be present without the method.
 *
 * Returns null when no usable native bridge exists (normal browser / RH web iframe).
 */
function resolveRobinhoodBridge(): RobinhoodBridge | null {
  if (typeof window === 'undefined') return null

  const w = window as WindowWithRobinhood

  const iosHandler = w.webkit?.messageHandlers?.onSageExit
  if (iosHandler?.postMessage) {
    return { platform: 'ios', iosHandler }
  }

  const androidHandler = w.android?.onSageExit
    ? w.android
    : w.GoldSageJavaScriptInterface?.onSageExit
      ? w.GoldSageJavaScriptInterface
      : null
  if (androidHandler) {
    return { platform: 'android', androidHandler }
  }

  return null
}

interface DispatchRobinhoodRedirectEventPayload {
  suppressPayload?: boolean
}

export interface QuestionnaireModel {
  // State
  slug: string
  active: Page
  pages: Page[]
  fBLeadId: string
  resumePageName: string | null
  setResumePageName: Action<QuestionnaireModel, string | null>
  pageIndex: number
  finished: boolean
  landingUrl: string
  referrerUrl: string
  isSubmitting: boolean
  bankrateLeadId: string
  directMailerOfferCode: string
  isSimpleV1Enabled: boolean
  simpleFlowChosen: 'fannie' | 'plaid' | 'fannie-plaid'
  errorPages: ErrorPage[]
  ratesPage: RateSelectPage
  successPages: SuccessPage[]
  questionnaireType: QuestionnaireType
  redirectInterstitial: RedirectInterstitial
  searchString: string
  isRobinhoodGoldMember: boolean
  isRobinhoodConcierge: boolean
  robinhoodRedirectToken: string
  ssnQuestionTouchedStatusSubmitted: boolean
  accountLinkingTouchedStatusSubmitted: boolean
  consentExperimentVariant: 'control' | 'treatment'
  consentExperimentEvaluated: boolean
  hasStartedSimpleV2Lifecycle: boolean
  // Computed
  isStart: Computed<QuestionnaireModel, boolean, StoreModel>
  shouldShowRates: Computed<QuestionnaireModel, boolean, StoreModel>
  isSplitFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isPrePricingFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isAmazonBenefitsFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isRobinhoodGoldFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isInRobinhoodApp: Computed<QuestionnaireModel, boolean, StoreModel>
  isInIosWebview: Computed<QuestionnaireModel, boolean, StoreModel>
  isInAndroidWebview: Computed<QuestionnaireModel, boolean, StoreModel>
  isLoanMatchFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isApplyFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isDecsDemsFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isPlaidFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isPhoneFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isFullFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isMiniFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isSsnFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isHomeEquityFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isSimpleFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isSimpleV2Flow: Computed<QuestionnaireModel, boolean, StoreModel>
  isLeadFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isFannieFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isSimpleV2FannieFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  isForceFannieFlow: Computed<QuestionnaireModel, boolean, StoreModel>
  statusesMap: Computed<QuestionnaireModel, StatusesMap, StoreModel>
  currentPageName: Computed<QuestionnaireModel, string>
  // Actions
  setSlug: Action<QuestionnaireModel, string>
  setPages: Action<QuestionnaireModel, Page[]>
  setActive: Action<QuestionnaireModel, Page>
  setFBLeadId: Action<QuestionnaireModel, string>
  setDirectMailerOfferCode: Action<QuestionnaireModel, string>
  setPageIndex: Action<QuestionnaireModel, number>
  setFinished: Action<QuestionnaireModel, boolean>
  setRatesPage: Action<QuestionnaireModel, RateSelectPage>
  setErrorPages: Action<QuestionnaireModel, ErrorPage[]>
  setSuccessPages: Action<QuestionnaireModel, SuccessPage[]>
  setIsSubmitting: Action<QuestionnaireModel, boolean>
  setBankrateLeadId: Action<QuestionnaireModel, string>
  setRedirectInterstitial: Action<QuestionnaireModel, RedirectInterstitial>
  setSearchString: Action<QuestionnaireModel, string>
  setSimpleFlowChosen: Action<
    QuestionnaireModel,
    'fannie' | 'plaid' | 'fannie-plaid'
  >
  setIsRobinhoodGoldMember: Action<QuestionnaireModel, boolean>
  setIsRobinhoodConcierge: Action<QuestionnaireModel, boolean>
  resetQuestionnaire: Action<QuestionnaireModel, void>
  setRobinhoodRedirectToken: Action<QuestionnaireModel, string>
  setConsentExperimentVariant: Action<
    QuestionnaireModel,
    'control' | 'treatment'
  >
  setConsentExperimentEvaluated: Action<QuestionnaireModel, boolean>
  setHasStartedSimpleV2Lifecycle: Action<QuestionnaireModel, boolean>
  dispatchRobinhoodRedirectEvent: Action<
    QuestionnaireModel,
    DispatchRobinhoodRedirectEventPayload
  >
  initialize: Thunk<
    QuestionnaireModel,
    InitializeQuestionnairePayload,
    null,
    StoreModel
  >
  // actionOn
  onSetPageIndex: ActionOn<QuestionnaireModel, StoreModel>
  onSetSlug: ActionOn<QuestionnaireModel, StoreModel>
  // Thunks
  next: Thunk<QuestionnaireModel, undefined, object, StoreModel>
  closeMiniFlow: Thunk<
    QuestionnaireModel,
    CloseMiniFlowPayload,
    object,
    StoreModel
  >
  submit: Thunk<QuestionnaireModel, null, object, StoreModel>
  back: Thunk<QuestionnaireModel, object, object, StoreModel>
  navigateToPage: Thunk<QuestionnaireModel, string, object, StoreModel>
  answer: Thunk<QuestionnaireModel, object, object, StoreModel>
  getQuestionnaire: Thunk<QuestionnaireModel, string, object, StoreModel>
  getAdjacentPageIndex: Thunk<QuestionnaireModel, number, object, StoreModel>
  evaluateConsentExperimentForPage: Thunk<
    QuestionnaireModel,
    string | undefined,
    object,
    StoreModel
  >
  checkRobinhoodGoldStatus: Thunk<
    QuestionnaireModel,
    undefined,
    object,
    StoreModel
  >
  getRobinhoodRedirectToken: Thunk<
    QuestionnaireModel,
    string,
    object,
    StoreModel
  >
  navigateToUrl: Thunk<QuestionnaireModel, string, object, StoreModel>
  handoffNavigationToNative: Thunk<
    QuestionnaireModel,
    string,
    object,
    StoreModel
  >
  setSsnQuestionTouchedStatusSubmitted: Action<QuestionnaireModel, boolean>
  setAccountLinkingTouchedStatusSubmitted: Action<QuestionnaireModel, boolean>
  // ThunkOn
  handlePhoneAppRedirect: ThunkOn<QuestionnaireModel, null, StoreModel>
  handleInitialPage: ThunkOn<QuestionnaireModel, null, StoreModel>
  runConsentExperimentOnPageChange: ThunkOn<
    QuestionnaireModel,
    null,
    StoreModel
  >
  fireSimpleV2Anchor: ThunkOn<QuestionnaireModel, null, StoreModel>
  savePendingSimpleV2Progress: ThunkOn<QuestionnaireModel, null, StoreModel>
}

/**
 * This module is a function because we need to instantiate different Questionnaire class instance for every run.
 * It helps to create a blank slate every time a store instance gets created in a test.
 */
const questionnaire = (): QuestionnaireModel => {
  let pendingProgressSave = Promise.resolve()
  let pendingUnauthenticatedProgress: {
    applicationGuid: string
    flowKey: string
    pageName: string
  } | null = null

  const saveProgress = (progress: {
    applicationGuid: string
    flowKey: string
    pageName: string
  }) => {
    pendingProgressSave = pendingProgressSave.then(async () => {
      try {
        await http.put(
          `${environment.apiBaseUrl}/api/flow-progress`,
          progress,
          {
            headers: { csrf: getCSRFToken() },
            timeout: 5000,
          }
        )
      } catch {
        incense()
          .details({
            name: 'FlowProgressSaveError',
            message: 'Failed to save flow progress',
          })
          .safe({ flowKey: progress.flowKey })
          .warn()
      }
    })
  }

  return {
    questionnaireType: QuestionnaireType.FULL1003, // Default to prod flow

    slug: null,

    active: null,

    pages: [],

    errorPages: [],

    successPages: null,

    ratesPage: null,

    resumePageName: null,
    setResumePageName: action((state, name) => {
      state.resumePageName = name
    }),
    pageIndex: 0,

    redirectInterstitial: {
      component: 'RedirectInterstitial',
    },

    finished: false,

    bankrateLeadId: null,

    directMailerOfferCode: null,

    isSubmitting: false,

    fBLeadId: localStorage.getItem('fBLeadId'),

    referrerUrl: getReferrerUrl(),

    landingUrl: getLandingUrl(),

    searchString: null,
    isRobinhoodGoldMember: false,
    isRobinhoodConcierge: false,
    robinhoodRedirectToken: null,

    isSimpleV1Enabled: false,

    simpleFlowChosen: null,

    // SIMPLE flow statuses
    ssnQuestionTouchedStatusSubmitted: false,
    accountLinkingTouchedStatusSubmitted: false,

    // Consent UX experiment
    consentExperimentVariant: 'control',
    consentExperimentEvaluated: false,
    hasStartedSimpleV2Lifecycle: false,

    // Reset questionnaire state to prevent issues when switching between loan types
    resetQuestionnaire: action((state) => {
      pendingUnauthenticatedProgress = null
      state.active = null
      state.resumePageName = null
      state.pages = []
      state.pageIndex = 0
      state.finished = false
      state.errorPages = []
      state.successPages = null
      state.ratesPage = null
      state.consentExperimentVariant = 'control'
      state.consentExperimentEvaluated = false
      state.hasStartedSimpleV2Lifecycle = false
      // Don't reset slug, bankrateLeadId, directMailerOfferCode, etc.
    }),

    isStart: computed(
      [
        (state) => state.active,
        (state) => state.pages,
        (state, storeState) => storeState.answers.mergedAnswers,
        (state, storeState) => storeState.visibleIfConditions,
      ],
      (active, pages, mergedAnswers, visibleIfConditions) => {
        if (!active) return false

        const firstVisiblePage = pages?.find((page) => {
          const visibleIfCondition = page.visibleIfConstructor?.[0]

          return (
            !visibleIfCondition ||
            isQuestionVisible(
              visibleIfCondition,
              mergedAnswers,
              visibleIfConditions
            )
          )
        })

        return active.name === firstVisiblePage?.name
      }
    ),

    shouldShowRates: computed(
      [
        (state) => state.bankrateLeadId,
        (state) => state.finished,
        (_, storeState) => storeState.answers.answers,
        (_, storeState) => storeState.propertyInfo.avmEstimatedValue,
        (_, storeState) => storeState.application.applicationId,
      ],
      (bankrateLeadId, finished, answers, avmEstimatedValue, applicationId) => {
        const { loanPurpose } = answers

        let requiredQuestions = ['zipCode']

        if (loanPurpose === LoanPurpose.Refinance) {
          requiredQuestions.push('remainingBalance')
          if (!avmEstimatedValue) {
            requiredQuestions.push('estimatedValue')
          }
        }

        //If estimated value is not populated from initial answers because we don't have a bankrateLeadId,
        //we need to require estimatedValue from the user to get rates
        if (null == bankrateLeadId) {
          requiredQuestions = requiredQuestions.concat([
            'propertyType',
            'propertyUse',
          ])
        }

        const questionsAnswered = Object.keys(answers)

        const missingQuestions = []
        const wereAllRequiredQuestionsAnswered = requiredQuestions.every(
          (questionName) => {
            const questionWasAnswered = questionsAnswered.includes(questionName)
            if (!questionWasAnswered) {
              missingQuestions.push(questionName)
            }
            return questionWasAnswered
          }
        )

        if (finished && !wereAllRequiredQuestionsAnswered && applicationId) {
          rollbar.error('Missing required questions to show pricing.', {
            missingQuestions,
            applicationId,
          })
        }

        return wereAllRequiredQuestionsAnswered
      }
    ),

    isSplitFlow: computed([(state) => state.slug], (slug) => {
      // using a string here instead of the enum to account for personal/duplicated flows
      return isQuestionSet(/pre-pricing|apply|lead-/, slug)
    }),

    isPrePricingFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/pre-pricing|lead-/, slug)
    }),

    isApplyFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/apply/, slug)
    }),

    isDecsDemsFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(QuestionSetSlug.DecsDems, slug)
    }),

    isPlaidFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(QuestionSetSlug.Plaid, slug)
    }),

    isAmazonBenefitsFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(QuestionSetSlug.AmazonBenefits, slug)
    }),

    // If a question set starts with rh-pre-pricing- or rh-apply- (or home-equity), it's a Robinhood Gold flow.
    // Other rh- slugs (e.g. rh-concierge) are distinct flows and must not be treated as Gold.
    isRobinhoodGoldFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/^rh-(pre-pricing|apply|home-equity)/, slug)
    }),

    isInIosWebview: computed([(state) => state.slug], (slug) => {
      if (typeof window === 'undefined') return false

      return !!(window as WindowWithRobinhood)?.webkit?.messageHandlers
        ?.onSageExit
    }),

    isInAndroidWebview: computed([(state) => state.slug], (slug) => {
      if (typeof window === 'undefined') return false

      // Robinhood exposes the Android bridge as either window.android (standard)
      // or window.GoldSageJavaScriptInterface (Gold shell); both are Android.
      return !!(
        (window as WindowWithRobinhood)?.android?.onSageExit ||
        (window as WindowWithRobinhood)?.GoldSageJavaScriptInterface?.onSageExit
      )
    }),

    isInRobinhoodApp: computed(
      [(state) => state.isInIosWebview, (state) => state.isInAndroidWebview],
      (isInIosWebview, isInAndroidWebview) => {
        return isInIosWebview || isInAndroidWebview
      }
    ),

    isLoanMatchFlow: computed([(state) => state.slug], (slug) => {
      const urlParams = new URLSearchParams(window.location.search)
      const loValue = urlParams.get('loan-match')

      return loValue === '1'
    }),

    isPhoneFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/phone/, slug)
    }),

    isFullFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/^full-.*/, slug)
    }),

    isMiniFlow: computed([(state) => state.slug], (slug) => {
      return isMiniFlowActive()
    }),

    isSsnFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/ssn/, slug) || isQuestionSet(/credit-consent/, slug)
    }),

    isHomeEquityFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/home-equity/, slug)
    }),

    isSimpleFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/simple/, slug)
    }),
    isSimpleV2Flow: computed([(state) => state.slug], (slug) => {
      return (
        slug === QuestionSetSlug.SimpleV2Purchase ||
        slug === QuestionSetSlug.SimpleV2Refinance ||
        slug === QuestionSetSlug.SimpleV2PurchaseFannie ||
        slug === QuestionSetSlug.SimpleV2RefinanceFannie
      )
    }),

    isSimpleV2FannieFlow: computed([(state) => state.slug], (slug) => {
      return (
        slug === QuestionSetSlug.SimpleV2PurchaseFannie ||
        slug === QuestionSetSlug.SimpleV2RefinanceFannie
      )
    }),

    isLeadFlow: computed([(state) => state.slug], (slug) => {
      return isQuestionSet(/^lead-/, slug)
    }),

    currentPageName: computed(
      [(state) => state.pages, (state) => state.pageIndex],
      (pages, pageIndex) => pages?.[pageIndex]?.name ?? `page-${pageIndex}`
    ),

    isFannieFlow: computed([(state) => state.slug], (slug) => {
      // Bank-only Simple V2 variants retain the existing credit and completion paths.
      return (
        isQuestionSet(/fannie/, slug) &&
        slug !== QuestionSetSlug.SimpleV2PurchaseFannie &&
        slug !== QuestionSetSlug.SimpleV2RefinanceFannie
      )
    }),

    isForceFannieFlow: computed([(state) => state.slug], (slug) => {
      // Check for force-finicity demo parameter
      const urlParams = new URLSearchParams(window.location.search)
      const finicityDemo = urlParams.get('finicity-demo')
      const forceParam = urlParams.get('force-finicity')

      if (finicityDemo === 'true' || forceParam === 'true') {
        return true
      }

      return false
    }),

    // This differentiates statuses based on whether the user is in the
    // single-application Alfie flow vs the split flows for `Pre-Pricing`
    // and `Apply`
    statusesMap: computed((state) => {
      // Is `Pre-Pricing`/`Apply` version of the application
      if (state.isSplitFlow) {
        const isPrePricingFlow = state.isPrePricingFlow

        const touchedStatus = isPrePricingFlow
          ? Status.PrePricingFlowTouched
          : Status.ApplyFlowTouched

        const completedStatus = isPrePricingFlow
          ? Status.PrePricingFlowCompleted
          : Status.ApplyFlowCompleted

        return {
          lock: Status.PrePricingFlowCompleted,
          rates: Status.PrePricingRatesDisplayed,
          noRates: Status.PrePricingNoRatesDisplayed,
          touched: touchedStatus,
          completed: completedStatus,
        }
      }

      if (state.isPhoneFlow) {
        return {
          lock: Status.LockRequested,
          rates: Status.QRatesDisplayed,
          noRates: Status.QNoRatesDisplayed,
          touched: Status.PhoneFlowTouched,
          completed: Status.PhoneFlowCompleted,
        }
      }

      if (state.isFannieFlow) {
        return {
          touched: Status.FannieFlowTouched,
          completed: Status.FannieFlowCompleted,
        }
      }

      if (state.isPlaidFlow) {
        return {
          touched: Status.PlaidFlowTouched,
          completed: Status.PlaidFlowCompleted,
        }
      }

      if (state.isHomeEquityFlow) {
        return {
          touched: Status.HomeEquityFlowTouched,
          completed: Status.HomeEquityFlowCompleted,
          rates: Status.HomeEquityOffersDisplayed,
          noRates: Status.HomeEquityNoOffersFound,
          leadSubmitted: Status.HomeEquityLeadSubmitted,
          manualReview: Status.HomeEquityManualReview,
        }
      }

      return {
        lock: Status.LockRequested,
        rates: Status.QRatesDisplayed,
        noRates: Status.QNoRatesDisplayed,
        completed: Status.QFormCompleted,
      }
    }),

    onSetSlug: actionOn(
      (actions) => actions.setSlug,
      (state, target) => {
        const ntlSlug = QuestionSetSlug.NTL
        const isNtl = target.payload?.toLowerCase()?.includes(ntlSlug)

        if (isNtl || state.isSplitFlow) {
          state.questionnaireType = QuestionnaireType.NTL
        } else {
          state.questionnaireType = QuestionnaireType.FULL1003
        }
      }
    ),

    setPages: action((state, payload) => {
      state.pages = payload
    }),

    setErrorPages: action((state, payload) => {
      state.errorPages = payload
    }),

    setSuccessPages: action((state, payload) => {
      state.successPages = payload
    }),

    setRatesPage: action((state, payload) => {
      state.ratesPage = payload
    }),

    setRedirectInterstitial: action((state, payload) => {
      state.redirectInterstitial = payload
    }),

    setSlug: action((state, slug) => {
      state.slug = slug
    }),

    setSearchString: action((state, payload) => {
      state.searchString = payload
    }),

    setActive: action((state, activePage) => {
      state.active = activePage
    }),

    setPageIndex: action((state, pageIndex) => {
      state.pageIndex = pageIndex
    }),

    setFinished: action((state, isFinished) => {
      state.finished = isFinished
    }),

    setBankrateLeadId: action((state, payload) => {
      state.bankrateLeadId = payload
    }),

    setIsSubmitting: action((state, payload) => {
      state.isSubmitting = payload
    }),

    setFBLeadId: action((state, payload) => {
      try {
        localStorage.setItem('fBLeadId', payload)
        state.fBLeadId = payload
      } catch (e) {
        return
      }
    }),
    setDirectMailerOfferCode: action((state, payload) => {
      state.directMailerOfferCode = payload
    }),

    setIsRobinhoodGoldMember: action((state, payload) => {
      state.isRobinhoodGoldMember = payload
    }),

    setIsRobinhoodConcierge: action((state, payload) => {
      state.isRobinhoodConcierge = payload
    }),

    setRobinhoodRedirectToken: action((state, payload) => {
      state.robinhoodRedirectToken = payload
    }),

    setConsentExperimentVariant: action((state, payload) => {
      state.consentExperimentVariant = payload
    }),

    setConsentExperimentEvaluated: action((state, payload) => {
      state.consentExperimentEvaluated = payload
    }),

    setHasStartedSimpleV2Lifecycle: action((state, payload) => {
      state.hasStartedSimpleV2Lifecycle = payload
    }),

    setSimpleFlowChosen: action((state, payload) => {
      state.simpleFlowChosen = payload
    }),

    setSsnQuestionTouchedStatusSubmitted: action((state, payload) => {
      state.ssnQuestionTouchedStatusSubmitted = payload
    }),

    setAccountLinkingTouchedStatusSubmitted: action((state, payload) => {
      state.accountLinkingTouchedStatusSubmitted = payload
    }),

    dispatchRobinhoodRedirectEvent: action(
      (
        state,
        { suppressPayload = false }: DispatchRobinhoodRedirectEventPayload = {}
      ) => {
        const token = state.robinhoodRedirectToken || ''

        let cohesionIdParams = ''

        // Cohesion Standard Aliasing documentation https://redventures.atlassian.net/wiki/spaces/COHSN/pages/183107712/Implementation+Information+for+Developers
        const aliasSet = (window as any).tagular?.('getAliasSet')
        if (aliasSet?.transitId) {
          cohesionIdParams = `&transit_id=${aliasSet.transitId}`
        }

        // HELOC apps live on Figure after completion; tag the stored deeplink so
        // login-redirect can route returning taps to Figure even after token expiry.
        const flowParams = isQuestionSet(/home-equity/, state.slug)
          ? `&flow=${RhDeeplinkFlow.HomeEquity}`
          : ''

        const redirectUrl = `${environment.borrowerPortal.apiBaseUrl}/robinhood/login-redirect?redirect_token=${token}${cohesionIdParams}${flowParams}`
        const payload = suppressPayload ? {} : { redirect_url: redirectUrl }

        // Read the bridge live: the memoized webview computeds can be stale when
        // Robinhood injects the bridge after render.
        const bridge = resolveRobinhoodBridge()

        const iframeHandler = () => {
          window.parent.postMessage(
            {
              type: 'onSageExit',
              payload,
            },
            '*'
          )
        }

        try {
          if (bridge?.platform === 'ios') {
            bridge.iosHandler.postMessage(payload)
          } else if (bridge?.platform === 'android') {
            // Robinhood expects a stringified payload for Android only
            bridge.androidHandler.onSageExit(JSON.stringify(payload))
          } else {
            // The user is not in an iOS or Android app, so we're assuming they are in a browser
            // NOTE: the user MUST be in a Robinhood specific flow for this function to have been
            // called, and the browser flow should only ever be loaded in an iframe on the
            // Robinhood web page
            iframeHandler()

            if (window.parent === window) {
              // If the window's parent is itself, we're not in an iframe
              // If the user is not in an iframe, we need to redirect to the redirect URL
              window.location.href = redirectUrl
            }
          }
        } catch (error) {
          rollbar.error('Failed to dispatch Robinhood redirect event', {
            error,
            platform: bridge?.platform,
          })

          // Default to iframe handler
          iframeHandler()
        }
      }
    ),

    /**
     * This action improves handling of navigation to external URLs in the webview (ex tel: or mailto:)
     * Specifically when inside a webview in a native iOS or Android app. We need to emit the onSageExit
     * message to the parent
     *
     * This is different than dispatchRobinhoodRedirectEvent because it does not need any authentication
     * It is just meant to redirect the user to the given URL.
     */
    navigateToUrl: thunk(async (actions, url) => {
      const enableSpecialAlfieLinkHandling = await variation(
        FEATURE_FLAG_TYPES.ENABLE_SPECIAL_ALFIE_LINK_HANDLING
      )

      if (!enableSpecialAlfieLinkHandling) {
        redirectTo(url)
        return
      }

      await actions.handoffNavigationToNative(url)
    }),

    /**
     * Hands a URL to the native Robinhood shell via the onSageExit bridge so the
     * app opens it (external browser / phone dialer). Direct window.location
     * navigation is swallowed inside the RH webview, so any required product
     * redirect must be delegated to the native app.
     *
     * Unlike navigateToUrl, this is intentionally NOT gated on
     * ENABLE_SPECIAL_ALFIE_LINK_HANDLING: it branches on isInRobinhoodApp
     * directly and falls back to standard navigation outside the webview.
     */
    handoffNavigationToNative: thunk((_, url) => {
      // Read the bridge live: the memoized isInRobinhoodApp computed can be stale
      // when Robinhood injects the bridge after render.
      const bridge = resolveRobinhoodBridge()

      if (!bridge) {
        redirectTo(url)
        return
      }

      const payload = { redirect_url: url }

      try {
        if (bridge.platform === 'ios') {
          bridge.iosHandler.postMessage(payload)
        } else {
          // Robinhood expects a stringified payload for Android only
          bridge.androidHandler.onSageExit(JSON.stringify(payload))
        }
      } catch (error) {
        rollbar.warn('Failed to hand off navigation to native app', {
          error,
          url,
          platform: bridge.platform,
        })

        // Fallback to standard navigation
        redirectTo(url)
      }
    }),

    onSetPageIndex: actionOn(
      (actions, storeActions) => actions.setPageIndex,
      (state, target) => {
        state.active = state.pages[target.payload]
      }
    ),

    runConsentExperimentOnPageChange: thunkOn(
      (actions) => actions.setPageIndex,
      async (actions) => {
        await actions.evaluateConsentExperimentForPage(undefined)
      }
    ),

    getQuestionnaire: thunk(
      async (
        { setSlug, initialize },
        questionSetSlug = QuestionSetSlug.NTL,
        { getStoreActions, getState, getStoreState }
      ) => {
        let timer
        let monarchEvaluation: MonarchEvaluation | null = null

        // Give Monarch some time to respond, otherwise just go with the current question set
        try {
          monarchEvaluation = (await Promise.race([
            getMonarchEvaluationResult(),
            new Promise((resolve) => {
              timer = setTimeout(() => resolve(null), 2000)
            }),
          ])) as MonarchEvaluation | null
        } catch (error) {
          console.error('Error getting monarch evaluation', error)
        }

        clearTimeout(timer)

        const isRuleActive =
          monarchEvaluation?.source?.trafficFlow?.isRuleActive
        const newQuestionSetSlug =
          monarchEvaluation?.source?.trafficFlow?.value?.['question-set']

        if (isRuleActive && newQuestionSetSlug) {
          if (newQuestionSetSlug !== questionSetSlug) {
            // Update the question set slug to the one in the monarch evaluation
            // Also update the URL to be the same question set slug
            questionSetSlug = newQuestionSetSlug

            // Replace the original question set param from the URL
            const urlParams = new URLSearchParams(window.location.search)
            urlParams.set('question-set', questionSetSlug)
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`

            // Overwrite the URL so that we don't end up reloading the page
            window.history.replaceState({}, '', newUrl)

            // Update state with the new question set slug
            setSlug(questionSetSlug)
          }
        }

        const { bankrateLeadId, directMailerOfferCode } = getState()
        const { applicationGuid } = getStoreState().application
        const { tracking, answers } = getStoreActions()
        const { getLicensedStates } = getStoreActions().licensedStates
        const { getFigureUnlicensedStates } = getStoreActions().homeEquity
        const { isAuthenticated } = getStoreState().authentication

        await getLicensedStates()
        if (isQuestionSet(/home-equity/, questionSetSlug)) {
          await getFigureUnlicensedStates()
        }

        let questionnaireResponse: AxiosResponse

        const isPhoneFlow = isQuestionSet(/phone/, questionSetSlug)
        const isPhoneFlowEnabled = await variation(
          FEATURE_FLAG_TYPES.PHONE_FLOW_ENABLED
        )

        // The phone flow only hydrates answers after the borrower verifies;
        // setLeadAnswers must not guess a purpose over the stored one meanwhile.
        const skipsHydration = isPhoneFlow && isPhoneFlowEnabled

        let appGuid = applicationGuid

        if (skipsHydration) {
          appGuid = null
        }

        try {
          // If the application has a user and we are not authenticated, we expect to get back a userId
          // in which case we will redirect to the Borrower Portal login-and-continue page
          questionnaireResponse = await requestQuestionnaire(
            questionSetSlug,
            bankrateLeadId,
            appGuid,
            directMailerOfferCode,
            isAuthenticated
          )

          if (
            environment.productName !== 'sage-home-loans' &&
            questionnaireResponse.data?.errors?.length > 0
          ) {
            throw new Error(questionnaireResponse.data.errors[0].message)
          }

          if (
            questionnaireResponse.data?.data?.getAnswers?.userId &&
            !isAuthenticated
          ) {
            if (isMiniFlowActive()) {
              // The BP drawer owns authentication; navigating its iframe to
              // the portal login would nest BP inside itself.
              console.log(
                'AlfieBUG [getQuestionnaire] skipping BP login redirect in mini-flow'
              )
            } else {
              console.log(
                'AlfieBUG [getQuestionnaire] redirecting to BP login and continue'
              )
              redirectTo(
                `${environment.borrowerPortal.uiBaseUrl}/login-and-continue?continue-application-guid=${appGuid}`
              )
            }
          }
        } catch (error) {
          const errorObject = new CustomError(
            'GetQuestionnaireError',
            'Failed to fetch questionnaire from Storyblok'
          )

          const errorMetadata = {
            questionSetSlug,
            bankrateLeadId,
            applicationGuid,
            error,
            sensitive: { questionnaireResponse },
          }

          rollbar.error(errorObject, errorMetadata)

          newRelic.increment('get_questionnaire.fail', [
            { key: 'from_bankrate', value: (!!bankrateLeadId).toString() },
          ])

          throw error
        }

        let questionSet: QuestionSet
        let mergedAnswers

        try {
          setSlug(questionSetSlug)

          if (appGuid) {
            await getStoreActions().application.hydrateLeadSource()
          }

          const leadAnswers = questionnaireResponse.data.data.getAnswers
          answers.setLeadAnswers({
            ...leadAnswers,
            skippedHydration: skipsHydration,
          })

          questionSet = questionnaireResponse.data.data.getQuestionnaire
          mergedAnswers = getStoreState().answers?.mergedAnswers

          tracking.setFlowID(questionSet?.flowId)
        } catch (error) {
          const errorObject = new CustomError(
            'GetQuestionnaireError',
            'Failed to prefill, set slug and designate loanPurpose'
          )

          const errorMetadata = {
            questionSetSlug,
            bankrateLeadId,
            applicationGuid,
            error,
            sensitive: { mergedAnswers, questionnaireResponse },
          }

          rollbar.error(errorObject, errorMetadata)

          newRelic.increment('get_questionnaire.fail', [
            { key: 'from_bankrate', value: (!!bankrateLeadId).toString() },
          ])

          throw error
        }

        // OJO Demo only
        if (getStoreState().questionnaire.slug === 'compare-ntl-demo') {
          getStoreActions().leComparison.setIsDemoFlow(true)

          // Simulate purchase for pre-approval demo
          getStoreActions().answers.setAnswers({
            loanPurpose: LoanPurpose.Purchase,
            propertyPurchasePrice: 660000,
            creditScore: 740,
          })
        }

        // Amazon Demo only
        if (getStoreState().questionnaire.isAmazonBenefitsFlow) {
          getStoreActions().answers.setAnswers({
            firstName: 'Montgomery',
            lastName: 'Burns',
            refiReason: 'preappoval',
            purchaseStage: 'contract',
            loanPurpose: LoanPurpose.Purchase,
            propertyType: 'SingleFamily',
            propertyUse: 'PrimaryResidence',
            creditScore: 740,
          })
        }

        try {
          await initialize({
            questionSet,
          })
        } catch (error) {
          const errorObject = new CustomError(
            'GetQuestionnaireError',
            'Failed to initialize'
          )

          const errorMetadata = {
            questionSetSlug,
            bankrateLeadId,
            applicationGuid,
            error,
            sensitive: { mergedAnswers, questionnaireResponse },
          }

          rollbar.error(errorObject, errorMetadata)

          newRelic.increment('get_questionnaire.fail', [
            { key: 'from_bankrate', value: (!!bankrateLeadId).toString() },
          ])

          throw error
        }
      }
    ),

    initialize: thunk(
      async (actions, payload, { getStoreState, getStoreActions }) => {
        const { isMiniFlow, isPlaidFlow, isFannieFlow } =
          getStoreState().questionnaire
        if (isMiniFlow) {
          window.parent.postMessage(
            {
              name: MessageTypes.MiniFlowTaskStarted,
              skipStatusUpdate: isPlaidFlow || isFannieFlow,
            },
            '*'
          )
        }
        const { checkSplitFlowSessionLength } = getStoreActions().noTalkLock
        try {
          await checkSplitFlowSessionLength()
        } catch (e) {
          incense(e)
            .details({
              name: 'questionnaireInitialize',
              message: 'Error checking single session logic',
            })
            .sensitive({
              payload,
            })
            .error()
        }

        const { questionSet } = payload

        // If the API cannot find the question set then it will return a question set
        // with a flowId of 0. In this case we want to redirect to the landing page
        // where the borrower can select refinance or purchase to continue
        if (questionSet.flowId === '0') {
          redirectTo(`${Routes.APP_1003}/welcome`)
          return
        }

        const questionPages: Page[] = questionSet.pages || []
        const otherPages: OtherPage[] = questionSet.otherPages || []

        actions.setPages(questionPages)

        const {
          ErrorPage: errorPages,
          SuccessPage: successPages,
          RateSelectPage: ratesPages,
          RedirectInterstitial: interstitialPages,
        } = groupBy(otherPages, 'component')

        if (errorPages?.length) {
          actions.setErrorPages(errorPages as ErrorPage[])
        }

        if (successPages?.length) {
          actions.setSuccessPages(successPages as SuccessPage[])
        }

        if (ratesPages?.length) {
          actions.setRatesPage(ratesPages[0] as RateSelectPage)
        }

        if (interstitialPages?.length) {
          actions.setRedirectInterstitial(
            interstitialPages[0] as RedirectInterstitial
          )
        }

        // Build a sanitized search string that strips the internal page index (n)
        // and sensitive parameters like ssn to avoid leaking PII into the URL or propagating it
        const originalSearch =
          history?.location?.search || window?.location?.search || ''

        const params = new URLSearchParams(originalSearch)
        const sensitiveParams = ['ssn', 'coBorrowerSsn', 'co-borrower-ssn']

        // Remove internal and sensitive params
        params.delete('n')
        sensitiveParams.forEach((key) => params.delete(key))

        const sanitized = params.toString()
        const sanitizedSearch = sanitized ? `?${sanitized}` : ''

        // Persist sanitized search string for future navigations
        actions.setSearchString(sanitizedSearch)

        // If the current URL contains removed params, immediately sanitize the browser URL
        if (originalSearch && originalSearch !== sanitizedSearch) {
          const newUrl = `${window.location.pathname}${sanitizedSearch}${
            window.location.hash || ''
          }`
          window.history.replaceState({}, '', newUrl)
        }
      }
    ),

    answer: thunk(
      async ({ next }, payload: any, { getStoreActions, getStoreState }) => {
        const {
          questionnaire: { getRobinhoodRedirectToken },
          application,
          answers: { setAnswers },
          authentication: { checkAuthentication },
        } = getStoreActions()

        const {
          application: { applicationId, coBorrowerId, applicationGuid },
          answers: {
            mergedAnswers: { firstName: existingFirstName },
          },
          questionnaire: { pageIndex, isRobinhoodGoldMember, slug },
        } = getStoreState()

        if (payload.creditScore) {
          payload.creditScore = Number(payload.creditScore)
        }

        if (payload.coBorrowerCreditScore) {
          payload.coBorrowerCreditScore = Number(payload.coBorrowerCreditScore)
        }

        // Lead-purchase collects isUnderContract instead of purchaseStage.
        if (payload.isUnderContract === 'Yes' && !payload.purchaseStage) {
          payload.purchaseStage = PurchaseStage.Contract
        } else if (payload.isUnderContract === 'No' && !payload.purchaseStage) {
          payload.purchaseStage = null
        }

        if (!applicationId) {
          await application.initialize()
        }

        const { applicationId: newApplicationId } = getStoreState().application

        const primaryBorrowerSsn = payload.ssn
        const coBorrowerSsn = payload.coBorrowerSsn
        const createAccount = payload.createAccount

        if (primaryBorrowerSsn) {
          await ApplicationClient.saveSSNAlias(
            newApplicationId,
            primaryBorrowerSsn
          )

          // Save state that says we've succesfully saved an SSN
          // So we can skip the SSN page as the user navigates the app
          payload.hasSavedSSN = true

          delete payload.ssn // Prevent sending ssn in subsequent requests
        }

        if (coBorrowerSsn) {
          await ApplicationClient.saveCoBorrowerSSNAlias(
            newApplicationId,
            coBorrowerId,
            coBorrowerSsn
          )

          // Save state that says we've succesfully saved an SSN
          // So we can skip the SSN page as the user navigates the app
          payload.hasSavedCoBorrowerSSN = true

          delete payload.coBorrowerSsn // Prevent sending co borrower ssn in subsequent requests
        }

        const skipBorrowerPortal = await variation(
          FEATURE_FLAG_TYPES.SKIP_BORROWER_PORTAL
        )
        const isCreateAccountSaveFixEnabled = await variation(
          FEATURE_FLAG_TYPES.ALFIE_CREATE_ACCOUNT_SAVE_FIX_ENABLED
        )

        // Check if user already has an auth0Id before attempting to create account
        const existingAuth0Id = getStoreState()?.answers?.mergedAnswers?.auth0Id
        let accountError = null

        if (createAccount && !skipBorrowerPortal && !existingAuth0Id) {
          const isPasswordlessWorkflow = await variation(
            FEATURE_FLAG_TYPES.PASSWORDLESS_WORKFLOW
          )
          const accountInfoIndex = payload.createAccount.findIndex(
            (item) => item.email && (isPasswordlessWorkflow || item.password)
          )

          if (accountInfoIndex !== -1) {
            const { email, password } = payload.createAccount[accountInfoIndex]
            let response, auth0Id
            try {
              // Account for first name question being asked on the same page as the create account page
              const firstName = existingFirstName || payload?.firstName
              const lastName = payload?.lastName || ''
              const phone = payload?.phone || ''

              response = await ApplicationClient.createAuth0Account(
                email,
                password,
                firstName,
                lastName,
                phone,
                newApplicationId
              )
              auth0Id = response.data?.createAuth0Account?.auth0Id
            } catch (e) {
              rollbar.error(new CustomError('AuthError', e.message))
              accountError = new Error(
                'Oops, something went wrong creating your account. Try a different email address.'
              )
            }
            if (accountError && !isCreateAccountSaveFixEnabled) {
              // Old behavior throws the error, fix waits until after saving
              throw accountError
            }

            // set email answer here so we have it at the top level and don't need to ask again later
            payload.email = email

            if (response.errors) {
              if (
                response.errors[0].message === 'Error signing up user.' &&
                (environment.productName === 'sage' ||
                  environment.productName === 'sage-home-loans')
              ) {
                const loginUrl = `${
                  environment.borrowerPortal.uiBaseUrl
                }/login-continue-application?continue-application-guid=${applicationGuid}&continue-index=${
                  pageIndex + 1
                }${slug ? `&continue-question-set=${slug}` : ''}`

                // Use onclick to force navigation within webview instead of opening external browser
                const onclickHandler = `event.preventDefault(); window.location.href='${loginUrl}'; return false;`

                accountError = new CustomError(
                  'internallyThrownError',
                  `It looks like there's already an account associated with that email and/or phone number. Please go and <a class="info-block__link" href="${loginUrl}" onclick="${onclickHandler}">sign in</a>.`
                )
              } else if (
                response.errors[0].message === 'Error signing up user.' &&
                environment.productName === 'interest'
              ) {
                accountError = new Error(
                  "It looks like there's already an account associated with that email. Please log in."
                )
              } else {
                rollbar.error(
                  new CustomError('AuthError', response.errors[0].message)
                )
                accountError = new Error(
                  'Oops, something went wrong creating your account. Try a different email address.'
                )
              }
              if (accountError && !isCreateAccountSaveFixEnabled) {
                // Old behavior throws the error, fix waits until after saving
                throw accountError
              }
            }

            if (!accountError) {
              // Removes the user entered email and password from the create account array
              delete payload.createAccount[accountInfoIndex].email
              delete payload.createAccount[accountInfoIndex].password
              payload.auth0Id = auth0Id // setting to top level for visibleIf conditions on page level
            }
            payload.createAccount[accountInfoIndex] = {
              ...payload.createAccount[accountInfoIndex],
            } // spread operator retains answers if storyblok has more questions in createAccount component

            if (isRobinhoodGoldMember && !accountError) {
              const accessToken =
                response?.data?.createAuth0Account?.token?.access_token
              if (accessToken) {
                await getRobinhoodRedirectToken(accessToken)
              }
            }

            if (!accountError) {
              // Not awaited - delayResponse defers the write 2s, which would
              // land on every submit. useAuthenticationRecheck covers failures.
              void checkAuthentication({ delayResponse: true })
            }
          }
        }

        // Without account error, we can save via the store
        if (!accountError) {
          setAnswers(payload)
          await getStoreActions().application.save(payload)
        } else {
          // With account error, we can save the data via the API without updating the store
          await saveAnswersWithoutStoreUpdate({
            payload,
            applicationId: newApplicationId,
          })

          // Now we can throw the account error since we've saved the data
          throw accountError
        }
      }
    ),

    getAdjacentPageIndex: thunk(
      (actions, increment, { getState, getStoreState }) => {
        const { pageIndex, pages } = getState()
        const { answers, visibleIfConditions } = getStoreState()

        const nextPageIndex = pageIndex + increment
        const nextPage = pages[nextPageIndex]

        if (nextPage === undefined) {
          return false
        }

        if (increment < 0 && isApiLoaderPage(pages, nextPageIndex)) {
          return actions.getAdjacentPageIndex(increment + Math.sign(increment))
        }

        if (
          isVisibleIfConstructor(nextPage.visibleIfConstructor) &&
          !isQuestionVisible(
            nextPage.visibleIfConstructor[0],
            answers.mergedAnswers,
            visibleIfConditions
          )
        ) {
          return actions.getAdjacentPageIndex(increment + Math.sign(increment))
        }

        if (
          nextPage.visibleIf &&
          !isQuestionVisible(
            nextPage.visibleIf,
            answers.mergedAnswers,
            visibleIfConditions
          )
        ) {
          return actions.getAdjacentPageIndex(increment + Math.sign(increment))
        }

        return nextPageIndex
      }
    ),

    closeMiniFlow: thunk(async (_, { parentMessage }) => {
      window.parent.postMessage(
        parentMessage || {
          name: MessageTypes.MiniFlowTaskCompleted,
        },
        '*'
      )

      return
    }),

    next: thunk(
      async (actions, __, { getState, getStoreActions, getStoreState }) => {
        const { active, searchString } = getState()
        const { pageSections, tracking } = getStoreActions()
        const { isMiniFlow, isPrePricingFlow, isSimpleV2Flow } =
          getStoreState().questionnaire

        if (!isSimpleV2Flow) {
          tracking.formContinued()
        }

        if (active === null) {
          return
        }

        const nextPageIndex = await actions.getAdjacentPageIndex(1)

        const { applicationGuid } = getStoreState().application
        if (isSimpleV2Flow && applicationGuid) {
          const { pages, slug } = getState()
          const progress = {
            applicationGuid,
            flowKey: slug,
            pageName:
              nextPageIndex === false ? active.name : pages[nextPageIndex].name,
          }
          if (getStoreState().authentication.isAuthenticated) {
            saveProgress(progress)
          } else {
            pendingUnauthenticatedProgress = progress
          }
        }

        if (nextPageIndex !== false) {
          actions.setPageIndex(nextPageIndex)

          history.push({
            search: `${searchString ? searchString : '?'}&n=${nextPageIndex}`,
          })
        } else {
          // Questionnaire is finished
          actions.setFinished(true)

          // Pre-pricing is only a segment of the apply-flow task: skip the
          // completion message and let the finished handler close the drawer.
          if (isMiniFlow && !isPrePricingFlow) {
            window.parent.postMessage(
              {
                name: MessageTypes.MiniFlowTaskCompleted,
              },
              '*'
            )
            return
          }
        }

        const previousSection = active.section
        pageSections.updateSectionStatuses(previousSection)
      }
    ),

    // Not used in NTL flow - already have credit score and this will override statuses
    submit: thunk(
      async ({ setIsSubmitting }, __, { getStoreState, getStoreActions }) => {
        const { mergedAnswers } = getStoreState().answers
        const { statusesMap } = getStoreState().questionnaire
        const { softPullCredit, updateStatus } = getStoreActions().application

        setIsSubmitting(true)

        // If a credit score doesn't already exist on state, run soft credit pull
        if (!mergedAnswers.creditScore) {
          await softPullCredit(false)
        }

        await updateStatus(statusesMap.completed)

        setIsSubmitting(false)
        return
      }
    ),

    back: thunk(async (actions, _, { getState, getStoreActions }) => {
      const { pageSections } = getStoreActions()
      const { pages, pageIndex, searchString } = getState()

      const previousPageIndex = await actions.getAdjacentPageIndex(-1)

      if (
        typeof previousPageIndex === 'number' &&
        previousPageIndex !== pageIndex
      ) {
        actions.setPageIndex(previousPageIndex)

        history.push({
          search: `${searchString ? searchString : '?'}&n=${previousPageIndex}`,
        })

        pageSections.setSectionStatus({
          sectionName: pages[previousPageIndex]?.section,
          statusCode: SectionStatusCode.active,
        })
      }
    }),

    navigateToPage: thunk(
      async (actions, pageName, { getState, getStoreActions }) => {
        const { pageSections } = getStoreActions()
        const { pages, pageIndex, searchString } = getState()

        const target = pages.findIndex((page) => page.name === pageName)

        if (target < 0 || target === pageIndex) return

        actions.setPageIndex(target)
        history.push({
          search: `${searchString ? searchString : '?'}&n=${target}`,
        })
        pageSections.setSectionStatus({
          sectionName: pages[target]?.section,
          statusCode: SectionStatusCode.active,
        })
      }
    ),

    handlePhoneAppRedirect: thunkOn(
      (actions, storeActions) => [
        actions.setSlug,
        storeActions.answers.setAnswers,
      ],
      async (actions, target, { getState, getStoreState }) => {
        const { loanPurpose } = getStoreState().answers.answers
        const { slug } = getState()

        if (slug && loanPurpose) {
          try {
            // Redirect phone app users to apply flow if FF is off
            const isPhoneFlow = getStoreState().questionnaire.isPhoneFlow
            const isPhoneFlowEnabled = await variation(
              FEATURE_FLAG_TYPES.PHONE_FLOW_ENABLED
            )

            if (isPhoneFlow && !isPhoneFlowEnabled) {
              return redirectToOtherQuestionSet(
                loanPurpose === LoanPurpose.Purchase
                  ? QuestionSetSlug.ApplyPurchase
                  : QuestionSetSlug.ApplyRefinance
              )
            }
          } catch (e) {
            const { applicationGuid } = getStoreState().application

            incense(e)
              .details({
                name: 'handlePhoneAppRedirectError',
                message: 'Error during redirect for phone app',
              })
              .sensitive({
                applicationGuid,
                slug,
                loanPurpose,
              })
              .error()

            redirectTo(`${Routes.APP_1003}/error`)
          }
        }
      }
    ),

    handleInitialPage: thunkOn(
      (actions, storeActions) => [
        storeActions.answers.setIsPrefillingAnswers,
        actions.setPages,
      ],
      async (actions, target, { getState, getStoreState, getStoreActions }) => {
        const state = getState()
        const { visibleIfConditions, answers, application } = getStoreState()
        const { pageSections, tracking } = getStoreActions()

        const { pages, bankrateLeadId } = state
        const { applicationGuid } = application
        const { isPrefillingAnswers } = answers

        // If we're not prefilling, questionnaire pages have been loaded, and no active page... then compute initial active page based on visibleIfs
        if (isPrefillingAnswers === false && pages?.length && !state.active) {
          // Resolve concierge status before computing initial page so
          // pages gated on isRobinhoodConcierge are visible from the start
          if (state.isRobinhoodGoldFlow) {
            await actions.checkRobinhoodGoldStatus()
          } else if (state.slug === QuestionSetSlug.RhConcierge) {
            // rh-concierge is opened via a manual link with no Robinhood
            // session, so there's no gold-status API call to make. Set the
            // flag directly so visibleIfs and the concierge banner variant
            // behave correctly.
            actions.setIsRobinhoodConcierge(true)
          }

          // Re-read store state after async gold status check so computed
          // visibleIfConditions reflect the updated isRobinhoodConcierge
          const currentVisibleIfs = getStoreState().visibleIfConditions
          const currentMergedAnswers = getStoreState().answers.mergedAnswers

          let initialPageIndex = pages.findIndex((page) => {
            const visibleIfCondition = page.visibleIfConstructor?.[0]

            const pageIsVisible =
              !visibleIfCondition ||
              isQuestionVisible(
                visibleIfCondition,
                currentMergedAnswers,
                currentVisibleIfs
              )

            return pageIsVisible
          })

          initialPageIndex = initialPageIndex >= 0 ? initialPageIndex : 0

          if (state.resumePageName) {
            const savedIndex = pages.findIndex(
              (page) => page.name === state.resumePageName
            )
            if (savedIndex < 0) {
              window.location.assign('/application-1003/error')
              return
            }
            initialPageIndex = savedIndex
          }

          actions.setPageIndex(initialPageIndex)
          pageSections.initializeSectionStatuses(getState().active?.section)

          try {
            tracking.formStarted()

            if (getState().isSimpleV2Flow) {
              actions.setHasStartedSimpleV2Lifecycle(true)
            }

            const attrs = [
              { key: 'from_bankrate', value: (!!bankrateLeadId).toString() },
              {
                key: 'is_iframe',
                value: (window.location !== window.parent?.location).toString(),
              },
            ]
            newRelic.increment('form_started', attrs)
          } catch (error) {
            const errorObject = new CustomError(
              'GetQuestionnaireError',
              'Failed to apply tracking and metrics'
            )

            const errorMetadata = {
              bankrateLeadId,
              applicationGuid,
              error,
              sensitive: { mergedAnswers: currentMergedAnswers },
            }

            rollbar.error(errorObject, errorMetadata)

            newRelic.increment('get_questionnaire.fail', [
              { key: 'from_bankrate', value: (!!bankrateLeadId).toString() },
            ])

            throw error
          }
        }
      }
    ),

    // Decoupled from SimplePage; listens to setPageIndex (set by handleInitialPage
    // after setActive) so resumed sessions record the actual initial step, not page-0.
    fireSimpleV2Anchor: thunkOn(
      (actions) => actions.setPageIndex,
      (_, target, { getStoreState, getStoreActions }) => {
        const questionnaire = getStoreState().questionnaire
        if (!questionnaire.isSimpleV2Flow) return

        const { formId } = getStoreState().tracking
        const pageIndex = target.payload as number
        const pageName =
          questionnaire.active?.name ??
          questionnaire.pages?.[pageIndex]?.name ??
          `page-${pageIndex}`

        simpleV2TrackingService.init(formId, pageName, pageIndex)

        if (!questionnaire.hasStartedSimpleV2Lifecycle) return

        const { tracking } = getStoreActions()
        tracking.formContinued()
      }
    ),

    savePendingSimpleV2Progress: thunkOn(
      (_, storeActions) => storeActions.authentication.setIsAuthenticated,
      (_, target) => {
        if (!target.payload || !pendingUnauthenticatedProgress) return
        saveProgress(pendingUnauthenticatedProgress)
        pendingUnauthenticatedProgress = null
      }
    ),

    evaluateConsentExperimentForPage: thunk(
      async (
        { setConsentExperimentVariant, setConsentExperimentEvaluated },
        pageName,
        { getState, getStoreState }
      ) => {
        const nameToCheck = pageName ?? getState().active?.name
        if (!nameToCheck || nameToCheck !== 'createAccount') return

        if (getState().consentExperimentEvaluated) return

        try {
          const hasPersistedConsent =
            getStoreState().answers?.applicationAnswers
              ?.tcpaTransactionalConsent

          if (hasPersistedConsent) {
            setConsentExperimentVariant('control')
          } else {
            const { applicationGuid } = getStoreState().application
            const questionSetSlug = getState().slug

            const consentVariant = await evaluateConsentExperiment({
              applicationGuid,
              questionSetSlug,
            })
            setConsentExperimentVariant(consentVariant)
          }
        } catch (error) {
          console.error('Error evaluating consent experiment:', error)
          setConsentExperimentVariant('control')
        } finally {
          setConsentExperimentEvaluated(true)
        }
      }
    ),

    checkRobinhoodGoldStatus: thunk(
      async (actions, _, { getState, getStoreState }) => {
        const { applicationGuid } = getStoreState().application

        if (!applicationGuid) return

        try {
          const response = await fetchRobinhoodGoldStatus(applicationGuid)
          if (response.status !== 200) {
            throw new Error('Failed to fetch Robinhood Gold status')
          }
          const data = response.data
          actions.setIsRobinhoodGoldMember(data.isGoldStatus)
          actions.setIsRobinhoodConcierge(data.isConcierge ?? false)
        } catch (error) {
          rollbar.error('Failed to check Robinhood Gold status', error)
          actions.setIsRobinhoodGoldMember(false)
          actions.setIsRobinhoodConcierge(false)
        }
      }
    ),

    getRobinhoodRedirectToken: thunk(
      async (actions, accessToken, { getState }) => {
        try {
          const redirectToken = await getRedirectToken(accessToken)
          if (redirectToken) {
            actions.setRobinhoodRedirectToken(redirectToken)
          }
        } catch (error) {
          rollbar.error('Failed to get Robinhood redirect token', error)
        }
      }
    ),
  }
}

export default questionnaire

export const questionnaireQuery = getGraphQLString(gql`
  fragment VisibleIfSingleConditionFragment on VisibleIfSingleCondition {
    question
    answer
    conditional
  }

  fragment QuestionBaseFragment on QuestionBase {
    title
    subTitle
    questionName {
      name
    }
    infoTitle
    infoContent
    infoWarning
    content
    visibleIf
    component
    modifierClass
    visibleIfConstructor {
      ... on VisibleIfPrebuiltCondition {
        name
      }
      ... on VisibleIfSingleCondition {
        ...VisibleIfSingleConditionFragment
      }
      ... on VisibleIfMultiCondition {
        operator
        conditions {
          ... on VisibleIfPrebuiltCondition {
            name
          }
          ... on VisibleIfSingleCondition {
            ...VisibleIfSingleConditionFragment
          }
          ... on VisibleIfMultiCondition {
            operator
            conditions {
              ... on VisibleIfPrebuiltCondition {
                name
              }
              ... on VisibleIfSingleCondition {
                ...VisibleIfSingleConditionFragment
              }
            }
          }
        }
      }
    }
    validations {
      validatorType
      rules {
        name
        params
      }
      condition {
        reference
        is
      }
    }
  }

  fragment OptionFragment on Option {
    text
    value
    inputField {
      ...QuestionBaseFragment
    }
    subOptions {
      ...QuestionBaseFragment
      defaultOption
      multiAnswerEnabled
      modifierClass
      options {
        text
        value
        inputField {
          ...QuestionBaseFragment
        }
      }
    }
  }

  fragment QuestionSwitch on Questions {
    ... on QuestionHidden {
      ...QuestionBaseFragment
      value
    }
    ... on QuestionToggle {
      ...QuestionBaseFragment
      toggled
    }
    ... on QuestionCheckbox {
      ...QuestionBaseFragment
      checked
      modifierClass
    }
    ... on QuestionCheckboxMulti {
      ...QuestionBaseFragment
      defaultOption
      multiAnswerEnabled
      modifierClass
      bottomContent
      content
      options {
        ...OptionFragment
      }
    }
    ... on QuestionSelect {
      ...QuestionBaseFragment
      modifierClass
      options {
        text
        value
      }
    }
    ... on QuestionMultiSelectButton {
      ...QuestionBaseFragment
      modifierClass
      options {
        ...OptionFragment
        iconName
      }
    }
    ... on QuestionStateSelect {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionRadio {
      ...QuestionBaseFragment
      modifierClass
      options {
        text
        value
      }
      defaultValue
    }
    ... on QuestionBinaryButton {
      ...QuestionBaseFragment
      defaultYes
    }
    ... on QuestionMultiButton {
      ...QuestionBaseFragment
      options {
        text
        value
      }
    }
    ... on QuestionIncome {
      ...QuestionBaseFragment
      options {
        text
        value
      }
    }

    ... on QuestionInput {
      ...QuestionBaseFragment
      modifierClass
      inputType
      maxLength
      placeholder
    }
    ... on QuestionAddressAutoComplete {
      ...QuestionBaseFragment
      maxLength
      placeholder
    }
    ... on QuestionAddressFieldsAutoComplete {
      ...QuestionBaseFragment
      maxLength
      placeholder
    }
    ... on QuestionRateSelection {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionNTLRateSelection {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionCompareLERateSelection {
      ...QuestionBaseFragment
      modifierClass
      redirectToSlug
    }
    ... on QuestionHomeEquityOfferSelection {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionCreateAccount {
      ...QuestionBaseFragment
    }
    ... on QuestionAPICallLoader {
      ...QuestionBaseFragment
      apiCall
      minimumTimeout
      errorMessage
      continueOnError
    }
    ... on QuestionUploader {
      ...QuestionBaseFragment
      topSubHeader
      uploadButtonText
      uploadErrorText
      documentType
    }
    ... on QuestionInfoAccordion {
      ...QuestionBaseFragment
      accordionContent
    }
    ... on QuestionSSN {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionPlaidAssets {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleSuccess {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionAUSResults {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionAmazonBenefits {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionFinicityLink {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSSNPage {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionLoanOfficerIntro {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2Info {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2PlaidIntro {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2Offramp {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2DownPayment {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2Declarations {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2CreditLoad {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2FannieLoaders {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2PricingLoad {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2OfframpSuccess {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2OfframpFailed {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2AddressFields {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2ResidencyHistory {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2EmploymentHistory {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2CoBorrowerQuestion {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2IncomeAssignment {
      ...QuestionBaseFragment
      modifierClass
    }
    ... on QuestionSimpleV2CoBorrowerDeclarations {
      ...QuestionBaseFragment
      modifierClass
    }
  }

  query getQuestionnaire(
    $id: String!
    $bankrateLeadId: String
    $applicationGuid: String
    $directMailerOfferCode: String
    $hostName: String
    $isAuthenticated: Boolean
  ) {
    getAnswers(
      bankrateLeadId: $bankrateLeadId
      applicationGuid: $applicationGuid
      directMailerOfferCode: $directMailerOfferCode
      isAuthenticated: $isAuthenticated
    ) {
      bankrateAnswers {
        id
        firstName
        lastName
        street
        city
        state
        zipCode
        email
        phone
        cashOut
        isTakingCashOut
        propertyType
        propertyUse
        loanPurpose
        estimatedValue
        remainingBalance
        currentMortgageBalance
        currentMortgageRate
        previouslySelectedRate {
          rate
          termInMonths
          loanAmount
        }
        propertyPurchasePrice
        propertyDownPayment
        declarationMilitary
        totalAssets
        grossAnnualIncome
        monthlyDebtsAmount
        propertyState
        propertyZip
        propertyCity
      }
      applicationAnswers {
        applicationId
        userId
        firstName
        lastName
        email
        phone
        street
        apt
        city
        state
        zipCode
        propertyUse
        propertyType
        propertyAttachment
        propertyNumberOfStories
        loanPurpose
        estimatedValue
        remainingBalance
        firstMortgageRate
        cashOut
        isTakingCashOut
        propertyPurchasePrice
        propertyDownPayment
        purchaseStage
        isUnderContract
        purchaseKnownAddress
        isCurrentAddress
        autoCompleteAddressFields
        tcpaTransactionalConsent
        tcpaMarketingAndCustomerCareConsent
        dateOfBirth
        expenseFirstMortgage
        expenseHomeownerInsurance
        expensePropertyTax
        expenseMortgageInsurance
        hasCoBorrower
        coBorrowerId
        coBorrowerPersonalInfo {
          firstName
          lastName
          email
          phone
        }
      }
      directMailerAnswers {
        firstName
        lastName
        street
        city
        state
        zipCode
      }
    }

    getQuestionnaire(id: $id, hostName: $hostName) {
      flowId
      otherPages {
        ... on RedirectInterstitial {
          component
          title
          body
          time
        }
        ... on ErrorPage {
          component
          name
          title
          body
          primaryCtaTitle
          primaryCtaLink {
            url
          }
        }
        ... on SuccessPage {
          component
          name
          title
          subtitle
          body
          primaryCtaTitle
          primaryCtaLink {
            url
          }
        }
        ... on RateSelectPage {
          component
          variations {
            variation
            title
            subTitle
            cta
          }
        }
      }
      pages {
        name
        section
        type
        title
        subTitle
        description
        visibleIf
        visibleIfConstructor {
          ... on VisibleIfPrebuiltCondition {
            name
          }
          ... on VisibleIfSingleCondition {
            ...VisibleIfSingleConditionFragment
          }
          ... on VisibleIfMultiCondition {
            operator
            conditions {
              ... on VisibleIfPrebuiltCondition {
                name
              }
              ... on VisibleIfSingleCondition {
                ...VisibleIfSingleConditionFragment
              }
              ... on VisibleIfMultiCondition {
                operator
                conditions {
                  ... on VisibleIfPrebuiltCondition {
                    name
                  }
                  ... on VisibleIfSingleCondition {
                    ...VisibleIfSingleConditionFragment
                  }
                }
              }
            }
          }
        }
        displaySumOfFields
        nextButtonText
        allowSkip
        modifierClass
        customPageValidation
        validationErrorMsg
        questions {
          ...QuestionSwitch
          ... on QuestionGroup {
            ...QuestionBaseFragment
            titleForRepeatButton
            textForRepeatButton
            textForDeleteButton
            autoRepeatValidationKey
            questions {
              ...QuestionSwitch
            }
          }
          ... on QuestionCreateAccount {
            ...QuestionBaseFragment
            questions {
              ...QuestionSwitch
            }
          }
        }
      }
    }
  }
`)

async function requestQuestionnaire(
  id: string,
  bankrateLeadId?: string,
  applicationGuid?: string,
  directMailerOfferCode?: string,
  isAuthenticated?: boolean
) {
  return await graphQL({
    query: questionnaireQuery,
    variables: {
      id,
      bankrateLeadId,
      applicationGuid,
      directMailerOfferCode,
      isAuthenticated,
      hostName: window.location.hostname,
    },
  })
}

function isQuestionSet(pattern: string | RegExp, slug: string) {
  const regex = new RegExp(pattern)
  return slug && regex.test(slug.toLowerCase())
}

function isApiLoaderPage(pages, pageIndex) {
  if (!pages?.length || !pageIndex) {
    return false
  }

  return pages?.[pageIndex]?.questions?.find(
    (question) => question?.questionName?.name === 'apiLoader'
  )
}

export async function fetchRobinhoodGoldStatus(applicationGuid: string) {
  if (!applicationGuid) {
    return null
  }

  return http.get(
    `${environment.apiBaseUrl}/api/robinhood/gold-status?applicationGuid=${applicationGuid}`
  )
}

/**
 * Save form answers directly to the API without updating the store.
 * This is used when account creation fails to prevent store updates
 * from triggering form re-renders that would clear the error message.
 */
const saveAnswersWithoutStoreUpdate = async ({
  payload,
  applicationId,
}: {
  payload: any
  applicationId: number
}) => {
  try {
    let savePayload = { ...payload }

    // Clean the payload by removing email/password from createAccount
    if (savePayload.createAccount) {
      savePayload.createAccount = savePayload.createAccount.map((item) => {
        const { email, password, ...rest } = item
        return rest
      })
    }

    // Remove unwanted answers like the save thunk does
    savePayload = removeUnwantedAnswers(savePayload)

    // Call the API directly to save the data without triggering store updates
    await ApplicationClient.fill(applicationId, savePayload)
  } catch (e) {
    // Log the error but don't let it prevent showing the account creation error
    incense(e)
      .details({
        name: 'SaveWithoutStoreUpdateFailure',
        message:
          'Failed to save form data directly to API when account creation failed',
      })
      .sensitive({ payload })
      .error()
  }
}
